import { OkeyTile } from "../types";

export class OkeyValidator {
  static getOkeyTileDefinition(indicator: OkeyTile) {
    if (!indicator || indicator.color === "fake") return null;
    const value = (indicator.value % 13) + 1;
    return { color: indicator.color, value };
  }

  static isOkey(tile: OkeyTile, okeyDef: { color: string; value: number } | null) {
    if (!okeyDef) return false;
    return tile.color === okeyDef.color && tile.value === okeyDef.value;
  }

  static getTileEffectiveValue(tile: OkeyTile, okeyDef: { color: string; value: number } | null) {
    if (tile.color === "fake") {
      return okeyDef ? { color: okeyDef.color, value: okeyDef.value } : { color: "fake", value: 0 };
    }
    return { color: tile.color, value: tile.value };
  }

  static isSubsetValid(subset: OkeyTile[], indicator: OkeyTile): boolean {
    if (!subset || subset.length < 3 || !indicator) return false;
    const okeyDef = this.getOkeyTileDefinition(indicator);
    if (!okeyDef) return false;
    
    const validColors = ["red", "black", "blue", "yellow"];
    let wildsCount = 0;
    const normalTiles: { color: string; value: number }[] = [];

    for (const t of subset) {
      if (!t) return false;
      if (this.isOkey(t, okeyDef)) {
        wildsCount++;
      } else {
        const eff = this.getTileEffectiveValue(t, okeyDef);
        const numVal = typeof eff.value === "number" ? eff.value : Number(eff.value);
        if (!validColors.includes(eff.color as string) || isNaN(numVal) || numVal < 1 || numVal > 13) {
          return false;
        }
        normalTiles.push({ color: eff.color as string, value: numVal });
      }
    }

    const grid = [
      new Array(15).fill(0), // Red
      new Array(15).fill(0), // Black
      new Array(15).fill(0), // Blue
      new Array(15).fill(0)  // Yellow
    ];

    for (const t of normalTiles) {
      const cIdx = validColors.indexOf(t.color as string);
      if (cIdx !== -1 && t.value >= 1 && t.value <= 13) {
        grid[cIdx][t.value]++;
      }
    }

    return this.solveNormalSets(grid, wildsCount);
  }

  static checkWin(rack: OkeyTile[], indicator: OkeyTile): { isWin: boolean; isDouble: boolean } {
    if (!rack || rack.length !== 14 || !indicator) return { isWin: false, isDouble: false };
    const okeyDef = this.getOkeyTileDefinition(indicator);
    if (!okeyDef) return { isWin: false, isDouble: false };
    
    const validColors = ["red", "black", "blue", "yellow"];
    let wildsCount = 0;
    const normalTiles: { color: string; value: number }[] = [];
    const pairMap = new Map<string, number>();

    for (const t of rack) {
      if (!t) return { isWin: false, isDouble: false };
      if (this.isOkey(t, okeyDef)) {
        wildsCount++;
      } else {
        const eff = this.getTileEffectiveValue(t, okeyDef);
        const numVal = typeof eff.value === "number" ? eff.value : Number(eff.value);
        if (!validColors.includes(eff.color as string) || isNaN(numVal) || numVal < 1 || numVal > 13) {
          // Masked, dummy, or invalid tile detected: cannot form a legitimate win
          return { isWin: false, isDouble: false };
        }
        normalTiles.push({ color: eff.color as string, value: numVal });
        const key = `${eff.color}-${numVal}`;
        pairMap.set(key, (pairMap.get(key) || 0) + 1);
      }
    }

    // Check pairs (Çifte gitme: 14 tiles forming exactly 7 pairs)
    let naturalPairs = 0;
    let singleTiles = 0;
    let hasInvalidTileCount = false;

    for (const count of pairMap.values()) {
      if (count === 2) {
        naturalPairs += 1;
      } else if (count === 1) {
        singleTiles += 1;
      } else if (count === 4) {
        naturalPairs += 2;
      } else if (count === 3) {
        naturalPairs += 1;
        singleTiles += 1;
      } else {
        // More than 4 duplicates or 0 cannot form a valid pair in standard 106-tile Okey
        hasInvalidTileCount = true;
      }
    }

    if (!hasInvalidTileCount && singleTiles <= wildsCount) {
      const remainingWilds = wildsCount - singleTiles;
      const totalPairs = naturalPairs + singleTiles + Math.floor(remainingWilds / 2);
      if (totalPairs === 7) {
        return { isWin: true, isDouble: true };
      }
    }

    // Check normal sets (Groups and Runs)
    const grid = [
      new Array(15).fill(0), // Red
      new Array(15).fill(0), // Black
      new Array(15).fill(0), // Blue
      new Array(15).fill(0)  // Yellow
    ];

    for (const t of normalTiles) {
      const cIdx = validColors.indexOf(t.color as string);
      if (cIdx !== -1 && t.value >= 1 && t.value <= 13) {
        grid[cIdx][t.value]++;
      }
    }

    const isWin = this.solveNormalSets(grid, wildsCount);
    return { isWin, isDouble: false };
  }

  private static solveNormalSets(grid: number[][], wilds: number): boolean {
    // Basic backtracking search
    // Find first non-empty cell
    let startC = -1;
    let startV = -1;
    outer: for (let c = 0; c < 4; c++) {
      for (let v = 1; v <= 13; v++) {
        if (grid[c][v] > 0) {
          startC = c;
          startV = v;
          break outer;
        }
      }
    }

    if (startC === -1) {
      // All normal tiles used. All wild tiles must also have been incorporated into valid sets.
      return wilds === 0; 
    }

    // Try forming a group (same value, different colors) starting with (startC, startV)
    // 3 or 4 tiles.
    for (let gSize = 3; gSize <= 4; gSize++) {
      const otherColors = [0, 1, 2, 3].filter(c => c !== startC);
      const subsets = this.getSubsets(otherColors, gSize - 1);
      
      for (const subset of subsets) {
        let neededWilds = 0;
        const colorsInGroup = [startC, ...subset];
        const taken: number[] = [];
        
        for (const c of colorsInGroup) {
          if (grid[c][startV] > 0) {
            taken.push(c);
          } else {
            neededWilds++;
          }
        }
        
        if (neededWilds <= wilds) {
          // Take them
          for (const c of taken) grid[c][startV]--;
          
          if (this.solveNormalSets(grid, wilds - neededWilds)) return true;
          
          // Backtrack
          for (const c of taken) grid[c][startV]++;
        }
      }
    }

    // Try forming a run (same color, consecutive values)
    // Runs can be length 3 to 13.
    // Must start at startV since this is the first non-empty value.
    // Also consider 12-13-1 special case.
    for (let rLength = 3; rLength <= 13; rLength++) {
      // A run starting at startV. Does it go out of bounds? 
      // If startV + rLength - 1 > 13, maybe it wraps to 1?
      // standard okey: 12, 13, 1 is valid, but 13, 1, 2 is not.
      // Let's build the sequence of values:
      const seq: number[] = [];
      let validSeq = true;
      for (let i = 0; i < rLength; i++) {
        let v = startV + i;
        if (v > 13) {
          if (v === 14 && startV !== 1) { // 1 can act as 14
            seq.push(1);
          } else {
            validSeq = false;
            break;
          }
        } else {
          seq.push(v);
        }
      }
      
      if (!validSeq) continue;

      let neededWilds = 0;
      const taken: number[] = [];

      for (const v of seq) {
        if (grid[startC][v] > 0) {
          taken.push(v);
        } else {
          neededWilds++;
        }
      }

      if (neededWilds <= wilds) {
        // Take them
        const counts = new Map();
        for (const v of taken) {
          counts.set(v, (counts.get(v) || 0) + 1);
          grid[startC][v]--;
        }
        
        if (this.solveNormalSets(grid, wilds - neededWilds)) return true;
        
        // Backtrack
        for (const [v, c] of counts.entries()) {
          grid[startC][v] += c;
        }
      }
    }

    // SPECIAL CASE: If startV === 1, try runs that END in 1 (e.g. 12-13-1)
    if (startV === 1) {
        for (let rLength = 3; rLength <= 13; rLength++) {
            const seq: number[] = [];
            for (let i = 0; i < rLength - 1; i++) {
                seq.push(14 - rLength + 1 + i);
            }
            seq.push(1);
            
            let neededWilds = 0;
            const taken: number[] = [];
            for (const v of seq) {
                if (grid[startC][v] > 0) {
                    taken.push(v);
                } else {
                    neededWilds++;
                }
            }
            if (neededWilds <= wilds) {
                const counts = new Map();
                for (const v of taken) {
                    counts.set(v, (counts.get(v) || 0) + 1);
                    grid[startC][v]--;
                }
                if (this.solveNormalSets(grid, wilds - neededWilds)) return true;
                for (const [v, c] of counts.entries()) {
                    grid[startC][v] += c;
                }
            }
        }
    }

    return false;
  }

  private static getSubsets(arr: number[], k: number): number[][] {
    if (k === 0) return [[]];
    if (arr.length === 0) return [];
    const [first, ...rest] = arr;
    const withFirst = this.getSubsets(rest, k - 1).map(s => [first, ...s]);
    const withoutFirst = this.getSubsets(rest, k);
    return [...withFirst, ...withoutFirst];
  }
}
